import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  type InputProps,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface AutocompleteInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
}

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  suggestions,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listboxId = useRef(`autocomplete-listbox-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const filtered = suggestions.filter(
    (s) => s.toLowerCase().includes(value.toLowerCase()) && s.toLowerCase() !== value.toLowerCase()
  );

  // Free-text values are kept as-is and become suggestions library-wide once saved (like tags).
  // Surface an explicit "Create 'X'" affordance when the typed value isn't an existing option.
  const trimmed = value.trim();
  const hasExactMatch = suggestions.some((s) => s.toLowerCase() === trimmed.toLowerCase());
  const showCreate = trimmed.length > 0 && !hasExactMatch;

  const selectItem = useCallback(
    (item: string) => {
      onChange(item);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter') {
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          e.preventDefault();
          selectItem(filtered[highlightedIndex]);
        } else if (showCreate) {
          // Accept the newly typed value and close the suggestions.
          e.preventDefault();
          selectItem(trimmed);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    },
    [filtered, highlightedIndex, selectItem, showCreate, trimmed]
  );

  const isOpen = showSuggestions && (filtered.length > 0 || showCreate);
  const activeDescendant = highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined;

  return (
    <Box position="relative">
      <Input
        ref={inputRef}
        value={value}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(e.target.value.length > 0);
          setHighlightedIndex(-1);
        }}
        onFocus={() => value.length > 0 && setShowSuggestions(true)}
        onBlur={() => {
          // Delay to allow click on suggestion
          if (blurTimerRef.current !== null) {
            clearTimeout(blurTimerRef.current);
          }
          blurTimerRef.current = setTimeout(() => setShowSuggestions(false), 150);
        }}
        onKeyDown={handleKeyDown}
        {...inputProps}
      />
      {isOpen && (
        <List
          id={listboxId}
          role="listbox"
          position="absolute"
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="sm"
          maxH="200px"
          overflowY="auto"
          w="100%"
          mt={1}
          data-testid="autocomplete-suggestions"
        >
          {filtered.map((item, idx) => (
            <ListItem
              key={item}
              id={`${listboxId}-option-${idx}`}
              role="option"
              aria-selected={idx === highlightedIndex}
              px={3}
              py={2}
              cursor="pointer"
              bg={idx === highlightedIndex ? 'blue.50' : undefined}
              _hover={{ bg: 'gray.50' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectItem(item)}
              data-testid={`suggestion-${item}`}
            >
              {item}
            </ListItem>
          ))}
          {showCreate && (
            <ListItem
              role="option"
              px={3}
              py={2}
              cursor="pointer"
              color="brand.500"
              fontWeight="600"
              borderTopWidth={filtered.length > 0 ? '1px' : 0}
              borderColor="gray.100"
              _hover={{ bg: 'gray.50' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectItem(trimmed)}
              data-testid="autocomplete-create-option"
            >
              {t('admin.library.createOption', { value: trimmed })}
            </ListItem>
          )}
        </List>
      )}
    </Box>
  );
};
